import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Row,
  Typography,
  Descriptions,
  Breadcrumb,
  Tooltip,
  Table,
  Tag,
  Spin,
  Modal,
  Input,
  Space,
} from 'antd';
import { useStoreActions, useStoreState } from '../store';
import { BulkArchiveResultModal } from '~/components/shared/BulkArchiveResultModal';
import { generateDeleteConfirmationPhrase } from '~/pages/AdminPages/ConfigurationAssets/utils';
import type { BulkArchiveResult } from '~/types/bulkArchive';
import { useCarriersFiltered } from '../hooks/queries/useCarriersFiltered';
import { useFulfillmentContexts } from '../hooks/queries/useFulfillmentContexts';
import { fulfillmentContextSummarizer } from '../store/fulfillmentContext/helpers';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom-v5-compat';
import { createUseStyles } from 'react-jss';
import { getContextStatus, ContextStatus } from '../components/fulfillmentContext/landing/utils';
import { carrierCountSummarizer } from '../store/fulfillmentContext/helpers';
import { prioritySort } from '../store/i18n/currencyUtils';
import type { ConfiguredCarrierCount, FulfillmentContextSummaryRecord } from '../store/fulfillmentContext/types';
import { FulfillmentContextOriginSummary } from '../components/fulfillmentContext/landing/FulfillmentContextOriginSummary';
import { CarriersColumn } from '../components/fulfillmentContext/landing/CarriersColumn';
import { ReadmeIcon } from '../components/assets/ReadmeIcon';
import { SHIPIUM_DOCUMENTATION_CONTEXTS_SLUG, SHIPIUM_DOCUMENTATION_URL } from './constants';
import { SearchBar } from '../components/shared/SearchBar';
import { escapeRegexChars } from '../utils/regex';
import { format, subDays } from 'date-fns';
import { FulfillmentContextTag, PAST_DAYS } from '../components/fulfillmentContext/landing/FulfillmentContextTag';
import { arrayToMap, useQueryCreator } from '../hooks/useQueryCreator';
import { partnerManagementService } from '../services';
import { ASSET_LIST_PAGE_SIZE } from '~/services/partner-management-service';
import { useFulfillmentContextActivity } from '../hooks/queries/useFulfillmentContextActivity';
import type { CarrierRateSet, Contract } from '../store/services/FP_FulfillmentContextService/types';
import { useSession } from '~/hooks/useSession';
import { getCarrierShortName } from '~/utils/carrierShortName';

// Minimal shape used by CarriersColumn
type CarrierRateMinimal = { carrierRateId?: string; carrierServiceMethodId?: string };

const { Text } = Typography;

const carrierRateMinimalToMap = (data: CarrierRateMinimal[]) => arrayToMap(data, 'carrierRateId');

export const useStyles = createUseStyles({
  buttonContainer: {
    display: 'flex',
    justifyContent: 'end',
    marginBottom: '5px',
    zIndex: '10',
  },
  carrierContainer: {
    display: 'flex',
    flexDirection: 'row',

    '@media (max-width: 1439px)': {
      flexDirection: 'column',
    },
  },
  carrierLabel: {
    marginRight: '5px',
    color: 'rgba(0, 0, 0, 0.85)',
  },
  tableStyles: {
    '&& table > .ant-table-thead > tr > th': {
      fontWeight: 'bold',
      background: 'unset',
    },
    '& .ant-table-tbody > tr.ant-table-row > td': {
      verticalAlign: 'top',
    },
    // disable sort background
    '& .ant-table-column-sort': {
      background: 'unset',
    },
  },
  searchFilterContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  searchFilterInput: {
    flex: '3',
  },
});

const FulfillmentContextLandingPage = () => {
  const carrierMap = useCarriersFiltered({ view: 'summary' });
  const sessionPartnerSlug = useStoreState((state) => state?.session?.user?.sessionPartnerUrl);
  const { isShipiumView } = useSession();
  const sessionPartnerId = useStoreState((state) => state.session.user)?.sessionPartnerId;
  const canEdit = useStoreState((state) => state.session.canEdit);
  const [searchBarText, setSearchBarText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [bulkResult, setBulkResult] = useState<BulkArchiveResult | null>(null);
  const bulkArchiveFulfillmentContexts = useStoreActions(
    (a) => a.configurationAssets.fulfillmentContexts.bulkArchiveFulfillmentContexts,
  );
  const classes = useStyles();
  const navigate = useNavigate();
  const params = useMemo(
    () => ({
      // sessionPartnerId will eventually always be defined
      // but TS does not understand that
      partnerId: sessionPartnerId,
      startDate: format(subDays(new Date(), PAST_DAYS), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
    }),
    [sessionPartnerId],
  );

  const {
    data: contexts = [],
    isLoading: loading,
    refetch: refetchContexts,
  } = useFulfillmentContexts({
    partnerId: sessionPartnerId || '',
    select: fulfillmentContextSummarizer,
  });

  const carrierRatesQuery = useQuery({
    ...useQueryCreator(partnerManagementService.carrierRates.searchSummaryPaginated, {
      args: [],
    }),
    enabled: !!sessionPartnerId,
  });

  const carrierRatesById = useMemo(
    () => (carrierRatesQuery.data ? carrierRateMinimalToMap(carrierRatesQuery.data.data) : {}),
    [carrierRatesQuery.data],
  );
  const carrierRatesTotalCount = carrierRatesQuery.data?.pagination.totalCount;
  const carrierRatesTruncated =
    carrierRatesTotalCount != null && (carrierRatesQuery.data?.data.length ?? 0) < carrierRatesTotalCount;

  const contractsQuery = useQuery({
    ...useQueryCreator(partnerManagementService.contracts.search, { args: [{ count: ASSET_LIST_PAGE_SIZE }] }),
    enabled: !!sessionPartnerId,
    select: (data) => arrayToMap(data, 'contractId') as Record<string, Contract>,
  });

  const rateSetsQuery = useQuery({
    ...useQueryCreator(partnerManagementService.carrierRateSets.search, { args: [] }),
    enabled: !!sessionPartnerId,
    select: (data) => arrayToMap(data, 'carrierRateSetId') as Record<string, CarrierRateSet>,
  });

  const carrierCountsByContextId = useMemo(() => {
    if (!rateSetsQuery.data || !contractsQuery.data || !contexts) {
      return {};
    }

    return contexts.reduce<Record<string, ConfiguredCarrierCount[]>>((result, ctx: FulfillmentContextSummaryRecord) => {
      const ids = ctx.contractIds || [];
      if (ids.length === 0) {
        result[ctx.contextId] = [];
        return result;
      }
      const contextContracts = ids.map((id) => contractsQuery.data?.[id]).filter(Boolean);
      result[ctx.contextId] = carrierCountSummarizer(
        contextContracts as Contract[],
        rateSetsQuery.data as Record<string, CarrierRateSet>,
      );
      return result;
    }, {});
  }, [contractsQuery.data, rateSetsQuery.data, contexts]);

  const [pageSize, setPageSize] = useState(50);

  const handlePageSizeChange = (current: number, size: number) => {
    setPageSize(size);
  };

  // Hoisted carrier selection summaries -> map by contextId
  const { data: recentSelectionCountByContextId = {}, isLoading: carrierSelectionsLoading } =
    useFulfillmentContextActivity({
      startDate: params.startDate,
      endDate: params.endDate,
      enabled: !!sessionPartnerId,
    });

  const renderTags = (context: FulfillmentContextSummaryRecord) => {
    if (Array.isArray(context.tags)) {
      const tags = context.tags.map((tag, i) => (
        <Tag key={`${context.contextId}-${tag}-${i}`} style={{ display: 'inline' }}>
          {tag}
        </Tag>
      ));
      return tags ? (
        <Descriptions.Item>
          <div>{tags}</div>
        </Descriptions.Item>
      ) : null;
    }
  };

  const carrierFilters = useMemo(() => {
    if (!contexts || !carrierCountsByContextId || !carrierMap) {
      return [];
    }

    const uniqueCarriers = new Set<string>();
    contexts.forEach((context) => {
      const carriers = carrierCountsByContextId[context.contextId] || [];
      carriers.forEach((carrier) => {
        const carrierName = getCarrierShortName(carrier.carrierId, carrierMap[carrier.carrierId]);
        uniqueCarriers.add(carrierName);
      });
    });

    return Array.from(uniqueCarriers)
      .sort()
      .map((carrier) => ({
        text: carrier,
        value: carrier,
      }));
  }, [contexts, carrierMap, carrierCountsByContextId]);

  const columns: ColumnsType<FulfillmentContextSummaryRecord> = [
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          Status
          {carrierSelectionsLoading && (
            <Spin size="small" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          )}
        </div>
      ),
      key: 'status',
      width: 200,
      filters: [
        { text: ContextStatus.ACTIVE, value: ContextStatus.ACTIVE },
        { text: ContextStatus.IDLE, value: ContextStatus.IDLE },
        { text: ContextStatus.INCOMPLETE, value: ContextStatus.INCOMPLETE },
      ],
      onFilter: (value, record) => {
        const { label } = getContextStatus({
          archived: record.archived,
          isValid: record.isValid,
          recentCarrierSelectionCount: recentSelectionCountByContextId[record.contextId] ?? -1,
        });
        return label === value;
      },
      sorter: (a, b) => {
        const aStatus = getContextStatus({
          archived: a.archived,
          isValid: a.isValid,
          recentCarrierSelectionCount: recentSelectionCountByContextId[a.contextId] ?? -1,
        }).label;
        const bStatus = getContextStatus({
          archived: b.archived,
          isValid: b.isValid,
          recentCarrierSelectionCount: recentSelectionCountByContextId[b.contextId] ?? -1,
        }).label;
        const statusPriority = [ContextStatus.ACTIVE, ContextStatus.IDLE, ContextStatus.INCOMPLETE];
        return prioritySort(statusPriority)(aStatus, bStatus);
      },
      render: (context: FulfillmentContextSummaryRecord) => {
        return (
          <FulfillmentContextTag
            contextId={context?.contextId}
            isValid={context.isValid}
            archived={context.archived}
            validationMessages={context.validationMessages}
            recentCarrierSelectionCount={recentSelectionCountByContextId[context.contextId] ?? -1}
          />
        );
      },
    },
    {
      title: 'Context',
      key: 'Context',
      width: 250,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.contextName?.localeCompare(b?.contextName),
      render: (context: FulfillmentContextSummaryRecord) => {
        return (
          <>
            <Button
              type={'link'}
              size={'large'}
              style={{
                height: 'auto',
                margin: 0,
                padding: 0,
                fontWeight: 'bold',
              }}
              onClick={() => navigate(`/${sessionPartnerSlug}/fulfillment-contexts/${context.contextId}`)}
            >
              <span
                style={{
                  whiteSpace: 'normal',
                  maxWidth: '250px',
                  textAlign: 'left',
                }}
              >
                {context.contextName}
              </span>
            </Button>
            <Descriptions size={'small'} column={1}>
              {context.tenantName && <Descriptions.Item label="Tenant">{context.tenantName}</Descriptions.Item>}
              {renderTags(context)}
            </Descriptions>
          </>
        );
      },
    },
    {
      title: 'Origin',
      key: 'origin',
      width: 375,
      sorter: (a, b) => (a.origin?.name ?? '').localeCompare(b?.origin?.name ?? ''),
      render: (context: FulfillmentContextSummaryRecord) => {
        return context.origin ? (
          <FulfillmentContextOriginSummary
            origin={context.origin}
            schedule={context.schedule}
            address={context.address}
            contextLink={`/${sessionPartnerSlug}/fulfillment-contexts/${context.contextId}/origin`}
          />
        ) : null;
      },
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          Carriers
          {(contractsQuery.isLoading || rateSetsQuery.isLoading || carrierRatesQuery.isLoading) && (
            <Spin size="small" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          )}
        </div>
      ),
      key: 'carriers',
      width: 350,
      filters: carrierFilters,
      onFilter: (value, record) => {
        const carriers = carrierCountsByContextId[record.contextId] || [];
        return carriers.some((carrier) => {
          const carrierName = getCarrierShortName(carrier.carrierId, carrierMap[carrier.carrierId]);
          return carrierName === value;
        });
      },
      render: (context: FulfillmentContextSummaryRecord) => {
        return (
          <CarriersColumn
            contextId={context.contextId}
            carriers={carrierCountsByContextId[context.contextId] || []}
            carrierRatesById={carrierRatesById}
          />
        );
      },
    },
  ];

  // Filter out archived contexts for non-Shipium users first
  const availableContexts = isShipiumView ? contexts : contexts?.filter((ctx) => !ctx.archived) || [];

  // Filter contexts based on search
  const filteredContexts = availableContexts.filter((record) => {
    const regex = new RegExp(escapeRegexChars(searchBarText), 'ig');
    const originNames = record.origin?.name ? record.origin.name : '';
    const { address1, address2, address3, companyName, country, postalCode, region } = record?.address?.location ?? {};
    const originAddresses = [address1, address2, address3, companyName, country, postalCode, region]
      .filter(Boolean)
      .join(' ');
    const carriers = (carrierCountsByContextId[record.contextId] || []).map((carrier) => carrier.carrierId).join(' ');
    return (
      regex.test(record.contextName) ||
      regex.test(originNames) ||
      regex.test(originAddresses) ||
      regex.test(carriers) ||
      (record?.tenantName && regex.test(record.tenantName)) ||
      regex.test(record?.tags?.join('') ?? '')
    );
  });

  const hiddenCount = availableContexts.length - filteredContexts.length;

  const confirmBulkArchive = (ids: string[]) => {
    let okDisabled = true;
    const phrase = generateDeleteConfirmationPhrase();
    const modal = Modal.confirm({
      title: `Archive ${ids.length} fulfillment context${ids.length !== 1 ? 's' : ''}?`,
      okButtonProps: { disabled: okDisabled },
      content: (
        <div>
          <p>
            This action cannot be undone. Type{' '}
            <b>
              <i>{phrase}</i>
            </b>{' '}
            to confirm.
          </p>
          <Input
            onChange={(e) => {
              const matches = e.target.value === phrase;
              if (matches !== !okDisabled) {
                okDisabled = !matches;
                modal.update({ okButtonProps: { disabled: okDisabled } });
              }
            }}
          />
        </div>
      ),
      onOk: async () => {
        const result = await bulkArchiveFulfillmentContexts({ ids });
        setSelectedRowKeys([]);
        setBulkResult(result);
        await refetchContexts();
      },
    });
  };

  return (
    <Card bodyStyle={{ paddingTop: 0 }}>
      {carrierRatesTruncated && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 8 }}
          message={`Carrier-rate data may be incomplete: ${carrierRatesQuery.data?.data.length} of ${carrierRatesTotalCount} rates loaded. Coverage stats below may be partial.`}
        />
      )}
      <div style={{ marginTop: 8 }}>
        <Row>
          <Col span={20}>
            <Breadcrumb separator=">">
              <Breadcrumb.Item>
                <a href={`/${sessionPartnerSlug}`}>
                  <Text>Home</Text>
                </a>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Text>
                  <b>Fulfillment Contexts</b>
                </Text>
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>
          <Col span={4} style={{ textAlign: 'right' }}>
            <Tooltip title={'View Documentation'} placement="left">
              <a
                rel="noreferrer"
                target="_blank"
                href={`${SHIPIUM_DOCUMENTATION_URL}/${SHIPIUM_DOCUMENTATION_CONTEXTS_SLUG}`}
              >
                <ReadmeIcon style={{ fontSize: '20px' }} />
              </a>
            </Tooltip>
          </Col>
        </Row>
      </div>
      <Divider orientation={'left'} style={{ marginTop: 5, marginBottom: 5 }} />
      <div className="card-container">
        <div className={classes.buttonContainer}>
          <Button
            icon={<PlusCircleOutlined />}
            onClick={() => navigate(`/${sessionPartnerSlug}/fulfillment-contexts/create`)}
            type={'primary'}
          >
            Add Fulfillment Context
          </Button>
        </div>

        {canEdit && selectedRowKeys.length > 0 && (
          <Space style={{ marginBottom: 8 }}>
            <Text>
              <strong>{selectedRowKeys.length}</strong> selected
            </Text>
            <Button danger onClick={() => confirmBulkArchive(selectedRowKeys.map(String))}>
              Archive {selectedRowKeys.length} selected
            </Button>
          </Space>
        )}

        <div className={classes.searchFilterContainer}>
          <div className={classes.searchFilterInput}>
            <SearchBar setSearchBarValue={setSearchBarText} placeholder={'Search Fulfillment Contexts'} />
          </div>
        </div>

        <div style={{ marginTop: 10, marginLeft: 5 }} hidden={hiddenCount === 0}>
          <Text italic>{hiddenCount} contexts are hidden due to current filters</Text>
        </div>

        <Divider orientation={'left'} style={{ marginTop: 15, marginBottom: 5 }} />

        <Table
          size={'large'}
          columns={columns}
          dataSource={filteredContexts}
          loading={loading}
          rowKey={(record) => record.contextId}
          rowSelection={canEdit ? { selectedRowKeys, onChange: setSelectedRowKeys } : undefined}
          onRow={(r) => ({
            onDoubleClick: () => {
              navigate(`/${sessionPartnerSlug}/fulfillment-contexts/${r.contextId}`);
            },
          })}
          bordered={false}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            onShowSizeChange: handlePageSizeChange,
          }}
          className={classes.tableStyles}
        />
        {bulkResult && (
          <BulkArchiveResultModal
            open
            entityLabel="Fulfillment Context"
            result={bulkResult}
            onClose={() => setBulkResult(null)}
          />
        )}
      </div>
    </Card>
  );
};

export default FulfillmentContextLandingPage;
